/**
 * Wall-mounted fixture collision detection.
 * Pure computation — no DB calls.
 *
 * Checks whether a wall-mounted fixture (mirror, sconce, light bar, etc.)
 * collides with parent wall bounds, adjacent walls, tall objects, openings,
 * ceiling, or floor.
 */

const {
  deriveWallEndpoints,
  getFloorY,
  getWallHeight,
  getWallInwardNormal,
} = require('./placementUtils');
const { hydrateRoomOpenings } = require('../openings/openingSync');
const { getObjectCorners } = require('../collision/geometry');

// ─── Geometry helpers ────────────────────────────────────────────────────────

/**
 * Get rotation-aware AABB dimensions using cos/sin projection.
 * Works for any y-rotation angle, not just cardinal (0°/90°/180°/270°).
 */
function getRotatedDimensions(dims, rotation) {
  if (!rotation || !dims) return dims;
  const rotY = rotation.y || 0;
  const cosR = Math.abs(Math.cos(rotY));
  const sinR = Math.abs(Math.sin(rotY));
  const hx = dims.x || 0;
  const hz = dims.z || 0;
  return {
    x: hx * cosR + hz * sinR,
    y: dims.y,
    z: hx * sinR + hz * cosR,
  };
}

/**
 * Project an object's XZ dimensions onto a wall's local axes using SAT.
 * Returns the half-extents along the wall tangent and wall normal.
 */
function getWallAlignedHalfExtents(dims, wallDir) {
  const halfX = (dims.x || 0) / 2;
  const halfZ = (dims.z || 0) / 2;
  return {
    alongHalf: Math.abs(halfX * wallDir.x) + Math.abs(halfZ * wallDir.z),
    perpHalf: Math.abs(halfX * wallDir.z) + Math.abs(halfZ * wallDir.x),
  };
}

/**
 * Project a 3D point onto a wall's axis.
 * @returns {{ t: number, wallLength: number } | null}
 */
function projectOntoWallAxis(point, wall) {
  const startEndpoints = deriveWallEndpoints(wall);
  const start = startEndpoints?.start;
  const end = startEndpoints?.end;
  if (!start || !end) return null;

  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const lengthSq = dx * dx + dz * dz;
  if (lengthSq === 0) return null;

  const t = ((point.x - start.x) * dx + (point.z - start.z) * dz) / lengthSq;
  return { t, wallLength: Math.sqrt(lengthSq) };
}

/**
 * Get OBB corners of an object in world space (XZ plane).
 *
 * Uses the same Y-rotation convention as `apply3DRotationTo2D` and the
 * Three.js / canvas renderers — `world = R(-rotY) * local` in the (x,z)
 * plane. Flipping the sin signs from a "naive" 2D CCW matrix is what
 * makes axis-aligned cases coincide with rotated-wall cases. Without
 * this convention, fixtures on non-axis-aligned walls reconstruct OBBs
 * mirrored across the wall direction, producing phantom parent-wall
 * overshoots in `checkParentWallBounds` / `checkExceedsParentWall`.
 */
function getOBBCorners(obj, margin) {
  const cx = obj.center.x || 0;
  const cz = obj.center.z || 0;
  const hw = (obj.dimensions.x || 0) / 2 + margin;
  const hd = (obj.dimensions.z || 0) / 2 + margin;
  const rotY = obj.rotation?.y || 0;
  const cos = Math.cos(rotY);
  const sin = Math.sin(rotY);

  return [
    { x: cx + hw * cos + hd * sin, z: cz - hw * sin + hd * cos },
    { x: cx - hw * cos + hd * sin, z: cz + hw * sin + hd * cos },
    { x: cx - hw * cos - hd * sin, z: cz + hw * sin - hd * cos },
    { x: cx + hw * cos - hd * sin, z: cz - hw * sin - hd * cos },
  ];
}

// ─── Overlap check primitives ────────────────────────────────────────────────
// Each returns { collides: boolean, reason: string, collidingElement: Object|null }

/**
 * How far off the parent wall's line a footprint vertex may sit and still
 * count as ON the wall. A deck seated flush lands within a millimetre or
 * two; a chamfer's far vertex is a hand's width away.
 */
const ON_WALL_TOLERANCE_M = 0.02;

/**
 * The vertices whose along-wall extent the parent-wall check measures.
 *
 * A box is what it looks like: all four corners. A footprint that carries
 * a `tub-surround-polygon` is not — a deck cut to a chamfered bay runs past
 * the end of the wall it hangs on, along the chamfer, on purpose, and its
 * far vertices say nothing about the wall. Only the vertices that lie ON the
 * wall's line are the fixture's contact with it, and those are what must
 * stay within the wall's ends. A polygon with no vertex on the wall at all
 * is measured whole, as the box would be.
 */
function verticesOnParentWall(obj, parentWall, margin, start, wallDir) {
  const poly = obj['tub-surround-polygon'];
  if (!(poly?.points?.length >= 5) || margin) return getOBBCorners(obj, margin);
  const all = getObjectCorners(obj);
  const onWall = all.filter((c) => {
    const rx = c.x - start.x;
    const rz = c.z - start.z;
    return Math.abs(rx * wallDir.z - rz * wallDir.x) <= ON_WALL_TOLERANCE_M;
  });
  return onWall.length >= 2 ? onWall : all;
}

/**
 * Check if a wall-mounted fixture extends beyond its parent wall's endpoints.
 */
function checkExceedsParentWall(obj, parentWall, margin) {
  const fixtureCenter = obj.center;
  const proj = projectOntoWallAxis(fixtureCenter, parentWall);
  if (!proj) return { collides: false, reason: '', collidingElement: null };

  const { t, wallLength } = proj;
  const startEndpoints = deriveWallEndpoints(parentWall);
  const start = startEndpoints?.start;
  const end = startEndpoints?.end;
  if (!start || !end)
    return { collides: false, reason: '', collidingElement: null };

  const wallDir = {
    x: (end.x - start.x) / wallLength,
    z: (end.z - start.z) / wallLength,
  };

  const corners = verticesOnParentWall(obj, parentWall, margin, start, wallDir);
  let minProj = Infinity;
  let maxProj = -Infinity;
  for (const c of corners) {
    const p = (c.x - start.x) * wallDir.x + (c.z - start.z) * wallDir.z;
    if (p < minProj) minProj = p;
    if (p > maxProj) maxProj = p;
  }
  // The measured span's ends, not a half-extent about the centre: a box is
  // symmetric about its centre and the two read the same, but a cut deck's
  // contact edge is not, and mirroring its longer side onto the shorter one
  // invented an overshoot on the end that was in fact flush with a wall end.
  const halfExtent = (maxProj - minProj) / 2 / wallLength;

  const eps = 0.001;
  if (minProj < -eps || maxProj > wallLength + eps) {
    const overshootLeft = Math.max(0, -minProj);
    const overshootRight = Math.max(0, maxProj - wallLength);
    const overshoot = Math.max(overshootLeft, overshootRight);
    console.log(
      `[checkExceedsParentWall] COLLISION:`,
      `wall=${parentWall.id?.slice(-8)}, wallLength=${wallLength.toFixed(3)}m,`,
      `wallBL=(${start.x.toFixed(3)},${start.z.toFixed(3)}) wallBR=(${end.x.toFixed(3)},${end.z.toFixed(3)}),`,
      `objCenter=(${fixtureCenter.x.toFixed(3)},${fixtureCenter.z.toFixed(3)}),`,
      `dims={x:${obj.dimensions?.x?.toFixed(3)}, z:${obj.dimensions?.z?.toFixed(3)}}, rot=${(obj.rotation?.y || 0).toFixed(3)},`,
      `t=${t.toFixed(4)}, halfExtent=${halfExtent.toFixed(4)},`,
      `overshoot=${(overshoot * 1000).toFixed(1)}mm (${(overshoot / 0.0254).toFixed(2)}in)`,
      overshootLeft > 0 ? '(LEFT end)' : '(RIGHT end)'
    );
    return {
      collides: true,
      reason: `Extends beyond parent wall (t=${t.toFixed(3)}, extent=${halfExtent.toFixed(3)}, overshoot=${(overshoot * 1000).toFixed(1)}mm / ${(overshoot / 0.0254).toFixed(2)}in)`,
      collidingElement: parentWall,
    };
  }
  return { collides: false, reason: '', collidingElement: null };
}

/**
 * Y rotation a wall-mounted fixture has when it hangs flat on `wall` — its
 * width runs along the wall. Used when a caller supplies centre + dims but
 * no fixture object (postProcessCollisionRemoval, placement probes).
 */
function wallMountedRotationY(wall) {
  // Derived, like every other endpoint read in this file. A bare `return 0`
  // when `alignment` is absent hangs the fixture on the world +X axis
  // regardless of which way the wall actually runs — and a scanned room has
  // no `alignment` on any wall, so that was every fixture in the room.
  // `wall.rotation.y` is the wall's own facing and the right answer here when
  // the endpoints can't be derived at all.
  const endpoints = deriveWallEndpoints(wall);
  if (!endpoints) return wall?.rotation?.y ?? 0;
  const a = endpoints.start;
  const b = endpoints.end;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  if (dx === 0 && dz === 0) return wall?.rotation?.y ?? 0;
  // local +x = (cos ry, -sin ry) must point along the wall tangent.
  return Math.atan2(-dz, dx);
}

/**
 * How far the checker forgives before it calls a fixture too close to a wall.
 * Shared by the check and its inverse so the two cannot disagree about the
 * boundary by an inch.
 */
const ADJACENT_WALL_TOLERANCE = 0.0254;

/**
 * Sub-micron shave so the inverse below lands strictly INSIDE the boundary.
 * Solving the check exactly produces a width the check then rejects on float
 * dust (0.5254 - 0.0254 comes out a bit over 0.5). A micron is orders of
 * magnitude below anything geometric here.
 */
const ADJACENT_WALL_EPSILON = 1e-6;

/**
 * A wall's axes in world XZ, snapped to the cardinal directions the rest of
 * this file assumes. `null` when the wall has no derivable endpoints, no
 * length, or is too short to be a real boundary.
 */
function adjacentWallAxes(wall) {
  const endpoints = deriveWallEndpoints(wall);
  const wallStart = endpoints?.start;
  const wallEnd = endpoints?.end;
  if (!wallStart || !wallEnd) return null;

  const wallHeight = wall.dimensions?.y || wall.height || 2.4;
  if (wallHeight < 1.0) return null;

  const segDx = wallEnd.x - wallStart.x;
  const segDz = wallEnd.z - wallStart.z;
  const segLen = Math.sqrt(segDx * segDx + segDz * segDz);
  if (segLen === 0) return null;

  const dirX = segDx / segLen;
  const dirZ = segDz / segLen;

  let normX = -dirZ;
  let normZ = dirX;

  const SNAP_THRESHOLD = 0.035;
  if (Math.abs(normX) < SNAP_THRESHOLD) normX = 0;
  if (Math.abs(normZ) < SNAP_THRESHOLD) normZ = 0;
  if (Math.abs(Math.abs(normX) - 1) < SNAP_THRESHOLD) {
    normX = Math.sign(normX);
    normZ = 0;
  }
  if (Math.abs(Math.abs(normZ) - 1) < SNAP_THRESHOLD) {
    normZ = Math.sign(normZ);
    normX = 0;
  }

  return { wallStart, dirX, dirZ, normX, normZ, segLen };
}

/**
 * The fixture's local axes in world XZ (three.js rotation about Y): its width
 * runs along `u`, its depth along `v`. Extents are projected onto each wall's
 * axes from these — a mirror rotated onto a wall along Z has its 4 mm depth,
 * not its 30" width, facing the wall behind it.
 */
function fixtureAxes(fixtureDims, rotationY) {
  const halfW = (fixtureDims.x || 0) / 2;
  const halfD = (fixtureDims.z || fixtureDims.x || 0) / 2;
  const u = { x: Math.cos(rotationY), z: -Math.sin(rotationY) };
  const v = { x: Math.sin(rotationY), z: Math.cos(rotationY) };
  return {
    halfW,
    halfD,
    u,
    v,
    extentAlong: (ax, az) =>
      Math.abs(halfW * (u.x * ax + u.z * az)) +
      Math.abs(halfD * (v.x * ax + v.z * az)),
  };
}

/**
 * Check if a wall-mounted fixture collides with any adjacent wall segment.
 */
function checkAdjacentWallOverlap(
  fixtureCenter,
  fixtureDims,
  walls,
  parentWallId,
  margin,
  allParentWallIds,
  rotationY = 0
) {
  const { extentAlong } = fixtureAxes(fixtureDims, rotationY);
  const skipIds = allParentWallIds || [parentWallId];

  for (const wall of walls) {
    if (skipIds.includes(wall.id)) continue;
    const axes = adjacentWallAxes(wall);
    if (!axes) continue;
    const { wallStart, dirX, dirZ, normX, normZ, segLen } = axes;

    const perpDist = Math.abs(
      (fixtureCenter.x - wallStart.x) * normX +
        (fixtureCenter.z - wallStart.z) * normZ
    );

    const projAlong =
      (fixtureCenter.x - wallStart.x) * dirX +
      (fixtureCenter.z - wallStart.z) * dirZ;
    const fixtureAlongExtent = extentAlong(dirX, dirZ);
    if (
      projAlong + fixtureAlongExtent < 0 ||
      projAlong - fixtureAlongExtent > segLen
    ) {
      continue;
    }

    const fixtureExtent = extentAlong(normX, normZ);
    const clearance = fixtureExtent + margin;

    if (perpDist < clearance - ADJACENT_WALL_TOLERANCE) {
      return {
        collides: true,
        reason: `Adjacent wall ${wall.id}: perpDist=${perpDist.toFixed(3)}m, needed=${clearance.toFixed(3)}m`,
        collidingElement: wall,
      };
    }
  }
  return { collides: false, reason: '', collidingElement: null };
}

/**
 * Widest this fixture may be before `checkAdjacentWallOverlap` rejects it.
 *
 * The inverse of the loop above, solved for the width: a wall constrains the
 * width only through `|halfW * (u · n)|`, so
 *
 *   halfW <= (perpDist + tolerance - margin - |halfD * (v · n)|) / |u · n|
 *
 * and a wall whose normal is perpendicular to the fixture's width axis
 * (`|u · n| ≈ 0`) bounds the depth instead, never the width.
 *
 * Callers SIZE with this and the checker JUDGES with the loop above, so the two
 * must stay in step — hence the shared `adjacentWallAxes` / `fixtureAxes` /
 * `ADJACENT_WALL_TOLERANCE`. `Infinity` means no adjacent wall bounds it.
 *
 * The in-range gate is evaluated at the fixture's CURRENT width. Narrowing can
 * only pull the fixture further inside the segment, so the bound is safe: it
 * may be tighter than strictly necessary, never looser.
 *
 * @returns {number} maximum full width in metres, or Infinity
 */
function maxWidthClearingAdjacentWalls(
  fixtureCenter,
  fixtureDims,
  walls,
  parentWallId,
  margin,
  allParentWallIds,
  rotationY = 0
) {
  const { halfD, u, v, extentAlong } = fixtureAxes(fixtureDims, rotationY);
  const skipIds = allParentWallIds || [parentWallId];
  let maxWidth = Infinity;

  for (const wall of walls) {
    if (skipIds.includes(wall.id)) continue;
    const axes = adjacentWallAxes(wall);
    if (!axes) continue;
    const { wallStart, dirX, dirZ, normX, normZ, segLen } = axes;

    const projAlong =
      (fixtureCenter.x - wallStart.x) * dirX +
      (fixtureCenter.z - wallStart.z) * dirZ;
    const fixtureAlongExtent = extentAlong(dirX, dirZ);
    if (
      projAlong + fixtureAlongExtent < 0 ||
      projAlong - fixtureAlongExtent > segLen
    ) {
      continue;
    }

    const widthAxisShare = Math.abs(u.x * normX + u.z * normZ);
    if (widthAxisShare < 1e-6) continue;

    const perpDist = Math.abs(
      (fixtureCenter.x - wallStart.x) * normX +
        (fixtureCenter.z - wallStart.z) * normZ
    );
    const depthShare = Math.abs(halfD * (v.x * normX + v.z * normZ));
    const allowedHalfW =
      (perpDist +
        ADJACENT_WALL_TOLERANCE -
        ADJACENT_WALL_EPSILON -
        margin -
        depthShare) /
      widthAxisShare;
    maxWidth = Math.min(maxWidth, Math.max(0, 2 * allowedHalfW));
  }

  return maxWidth;
}

/**
 * Map a tall room object type to a friendly noun for user-facing messages.
 * Used by `checkTallObjectOverlap` so the iOS / web toast renders as a
 * sentence the user can act on instead of leaking the raw object id and
 * AABB distances.
 */
function friendlyTallObjectReason(objType) {
  const t = (objType || '').toLowerCase();
  let noun = 'a tall fixture';
  if (t.includes('shower')) noun = 'the shower';
  else if (t.includes('bathtub') || t.includes('tub')) noun = 'the bathtub';
  else if (t.includes('storage')) noun = 'the storage cabinet';
  return `${noun} is blocking the placement`;
}

/**
 * Check if a wall-mounted fixture collides with tall room objects
 * (showers, storage, bathtubs > 1.5 m).
 */
function checkTallObjectOverlap(
  fixtureCenter,
  fixtureDims,
  objects,
  margin,
  excludeId,
  fixtureRotationY
) {
  const rotY = fixtureRotationY || 0;
  const cosR = Math.abs(Math.cos(rotY));
  const sinR = Math.abs(Math.sin(rotY));
  const fHalfW = fixtureDims.x / 2;
  const fHalfD = (fixtureDims.z || 0.1) / 2;
  const clearanceX = fHalfW * cosR + fHalfD * sinR + margin;
  const clearanceZ = fHalfW * sinR + fHalfD * cosR + margin;

  const tallObjectPatterns = ['shower', 'storage', 'bathtub', 'tub'];

  for (const obj of objects) {
    if (excludeId && obj.id === excludeId) continue;
    const objType = (obj.type || '').toLowerCase();
    const categoryKeys = obj.category ? Object.keys(obj.category) : [];
    const isTallType = tallObjectPatterns.some(
      (pattern) =>
        objType.includes(pattern) ||
        categoryKeys.some((k) => k.toLowerCase().includes(pattern))
    );
    const isTall = obj.dimensions?.y > 1.5;

    if (!isTallType || !isTall) continue;
    if (!obj.center) continue;

    const objRotY = obj.rotation?.y || 0;
    const objCosR = Math.abs(Math.cos(objRotY));
    const objSinR = Math.abs(Math.sin(objRotY));
    const oHalfW = (obj.dimensions?.x || 1) / 2;
    const oHalfD = (obj.dimensions?.z || 1) / 2;
    const objHalfX = oHalfW * objCosR + oHalfD * objSinR;
    const objHalfZ = oHalfW * objSinR + oHalfD * objCosR;

    const dx = Math.abs(fixtureCenter.x - obj.center.x);
    const dz = Math.abs(fixtureCenter.z - obj.center.z);

    if (dx < objHalfX + clearanceX && dz < objHalfZ + clearanceZ) {
      return {
        collides: true,
        reason: friendlyTallObjectReason(obj.type),
        collidingElement: obj,
      };
    }
  }
  return { collides: false, reason: '', collidingElement: null };
}

/**
 * Check if a wall-mounted fixture overlaps any opening (window, door, opening)
 * on the same parent wall.
 */
function checkOpeningOverlap(
  fixtureCenter,
  fixtureDims,
  openings,
  parentWall,
  margin
) {
  if (!openings || openings.length === 0) {
    return { collides: false, reason: '', collidingElement: null };
  }

  const parentWallId = parentWall.id;
  const fixtureProj = projectOntoWallAxis(fixtureCenter, parentWall);
  if (!fixtureProj) {
    return { collides: false, reason: '', collidingElement: null };
  }

  const fixtureAlong = fixtureProj.t * fixtureProj.wallLength;
  const pStartEndpoints = deriveWallEndpoints(parentWall);
  const pStart = pStartEndpoints?.start;
  const pEnd = pStartEndpoints?.end;
  const wLen = fixtureProj.wallLength;
  const wDir =
    pStart && pEnd
      ? { x: (pEnd.x - pStart.x) / wLen, z: (pEnd.z - pStart.z) / wLen }
      : { x: 1, z: 0 };
  const { alongHalf: fixtureAlongHalf } = getWallAlignedHalfExtents(
    fixtureDims,
    wDir
  );
  const fixtureLeft = fixtureAlong - fixtureAlongHalf;
  const fixtureRight = fixtureAlong + fixtureAlongHalf;
  const fixtureBottom = fixtureCenter.y - fixtureDims.y / 2;
  const fixtureTop = fixtureCenter.y + fixtureDims.y / 2;

  for (const opening of openings) {
    if (!opening.center || !opening.dimensions) continue;

    const openingWallId = opening.parentWallIds?.[0];
    if (openingWallId !== parentWallId) continue;

    const openingProj = projectOntoWallAxis(opening.center, parentWall);
    if (!openingProj) continue;

    const openingAlong = openingProj.t * openingProj.wallLength;
    const openingHalfW = (opening.dimensions.x || 0.9) / 2;
    const openingLeft = openingAlong - openingHalfW;
    const openingRight = openingAlong + openingHalfW;

    const hOverlap =
      fixtureLeft < openingRight + margin &&
      openingLeft < fixtureRight + margin;
    if (!hOverlap) continue;

    const openingBottom = opening.center.y - (opening.dimensions.y || 1.0) / 2;
    const openingTop = opening.center.y + (opening.dimensions.y || 1.0) / 2;
    const vOverlap =
      fixtureBottom < openingTop + margin &&
      openingBottom < fixtureTop + margin;

    if (vOverlap) {
      const openingType = opening.type || 'opening';
      return {
        collides: true,
        reason: `Overlaps ${openingType} ${opening.id} on parent wall`,
        collidingElement: opening,
      };
    }
  }
  return { collides: false, reason: '', collidingElement: null };
}

/**
 * Check if a wall-mounted fixture extends above the ceiling.
 */
function checkCeilingOverlap(fixtureCenter, fixtureDims, roomData, parentWall) {
  const floorY = getFloorY(roomData);
  const wallHeight = getWallHeight(parentWall);
  const ceilingY = floorY + wallHeight;
  const fixtureTop = fixtureCenter.y + fixtureDims.y / 2;

  if (fixtureTop > ceilingY + 0.0254) {
    return {
      collides: true,
      reason: `Extends above ceiling (top=${fixtureTop.toFixed(3)}m, ceiling=${ceilingY.toFixed(3)}m)`,
      collidingElement: { id: 'ceiling', type: 'ceiling' },
    };
  }
  return { collides: false, reason: '', collidingElement: null };
}

/**
 * Check if a wall-mounted fixture extends below the floor.
 */
function checkFloorOverlap(fixtureCenter, fixtureDims, roomData) {
  const floorY = getFloorY(roomData);
  const fixtureBottom = fixtureCenter.y - fixtureDims.y / 2;

  if (fixtureBottom < floorY - 0.01) {
    return {
      collides: true,
      reason: `Extends below floor (bottom=${fixtureBottom.toFixed(3)}m, floor=${floorY.toFixed(3)}m)`,
      collidingElement: { id: 'floor', type: 'floor' },
    };
  }
  return { collides: false, reason: '', collidingElement: null };
}

// ─── Orchestrator ────────────────────────────────────────────────────────────

/**
 * Check if a wall-mounted fixture collides with anything:
 *   1. Parent wall boundaries  (extends beyond wall)
 *   2. Adjacent walls          (too close to perpendicular wall)
 *   3. Tall objects            (showers, storage, bathtubs)
 *   4. Openings               (windows, doors, openings on same wall)
 *   5. Ceiling
 *   6. Floor
 *
 * @param {Object} fixtureCenter   - {x, y, z} center position
 * @param {Object} fixtureDims     - {x: width, y: height, z: depth}
 * @param {Object} roomData        - Room data (walls, objects, doors, windows, openings)
 * @param {Object} parentWall      - The wall the fixture is mounted on
 * @param {number} collisionMargin - Clearance margin (default 2 inches / 0.0508 m)
 * @param {string} [excludeId]     - Object ID to exclude from tall-object checks
 * @param {boolean} [excludeWindowOverlap=false] - Skip window overlap checks
 * @param {string[]} [allParentWallIds=null] - All parent wall IDs to skip in adjacent check
 * @param {Object} [fixtureObj=null] - Full fixture object for OBB parent-wall check
 * @returns {{ collides: boolean, reason: string, collidingElement: Object|null }}
 */
function checkWallFixtureOverlap(
  fixtureCenter,
  fixtureDims,
  roomData,
  parentWall,
  collisionMargin = 0.0508,
  excludeId,
  excludeWindowOverlap = false,
  allParentWallIds = null,
  fixtureObj = null,
  structureOpenings = null
) {
  // Slim room copies (post-PR #634) carry only id/structureOpeningId/etc.
  // Without hydration the opening loop's `!opening.center` guard silently
  // skips every door / window, so the fixture can be placed overlapping
  // any of them. Hydrate against canonical structure-level arrays when the
  // caller provides them.
  const effectiveRoomData = structureOpenings
    ? hydrateRoomOpenings(roomData, structureOpenings)
    : roomData;
  const walls = effectiveRoomData.walls || [];
  const objects = effectiveRoomData.objects || [];
  const openings = [
    ...(effectiveRoomData.doors || []).map((d) => ({
      ...d,
      _sourceType: 'door',
    })),
    ...(effectiveRoomData.windows || []).map((w) => ({
      ...w,
      _sourceType: 'window',
    })),
    ...(effectiveRoomData.openings || []).map((o) => ({
      ...o,
      _sourceType: 'opening',
    })),
  ];

  // 1. Parent wall bounds
  const wallCheck = checkExceedsParentWall(
    fixtureObj || { center: fixtureCenter, dimensions: fixtureDims },
    parentWall,
    0
  );
  if (wallCheck.collides) {
    console.log(`💡 Fixture overlap: ${wallCheck.reason}`);
    return wallCheck;
  }

  // 2. Adjacent walls
  // The extents are projected by rotation, so they must start from the
  // fixture's LOCAL dimensions — width along its own x, depth along its own
  // z. `validateBeforeSave` hands `fixtureDims` in as the rotation-aware
  // AABB (which the tall-object and opening checks below want), and for a
  // fixture turned a quarter turn that AABB has already swapped width and
  // depth; projecting it by the rotation again swapped them back, so a tub
  // on an east or west wall presented its 45" length to the back face of its
  // own partition and its 20" depth to the end walls it could actually run
  // into. The object, when given, is the local truth.
  const adjCheck = checkAdjacentWallOverlap(
    fixtureCenter,
    fixtureObj?.dimensions || fixtureDims,
    walls,
    parentWall.id,
    0,
    allParentWallIds,
    fixtureObj?.rotation?.y ?? wallMountedRotationY(parentWall)
  );
  if (adjCheck.collides) {
    console.log(`💡 Fixture overlap: ${adjCheck.reason}`);
    return adjCheck;
  }

  // 3. Tall objects
  const objCheck = checkTallObjectOverlap(
    fixtureCenter,
    fixtureDims,
    objects,
    0,
    excludeId,
    parentWall?.rotation?.y
  );
  if (objCheck.collides) {
    console.log(`💡 Fixture overlap: ${objCheck.reason}`);
    return objCheck;
  }

  // 4. Openings (windows, doors, openings)
  const effectiveOpenings = excludeWindowOverlap
    ? openings.filter(
        (o) =>
          (o.type || '').toLowerCase() !== 'window' &&
          o._sourceType !== 'window'
      )
    : openings;
  const openingCheck = checkOpeningOverlap(
    fixtureCenter,
    fixtureDims,
    effectiveOpenings,
    parentWall,
    collisionMargin
  );
  if (openingCheck.collides) {
    console.log(`💡 Fixture overlap: ${openingCheck.reason}`);
    return openingCheck;
  }

  // 5. Ceiling
  const ceilingCheck = checkCeilingOverlap(
    fixtureCenter,
    fixtureDims,
    effectiveRoomData,
    parentWall
  );
  if (ceilingCheck.collides) {
    console.log(`💡 Fixture overlap: ${ceilingCheck.reason}`);
    return ceilingCheck;
  }

  // 6. Floor
  const floorCheck = checkFloorOverlap(
    fixtureCenter,
    fixtureDims,
    effectiveRoomData
  );
  if (floorCheck.collides) {
    console.log(`💡 Fixture overlap: ${floorCheck.reason}`);
    return floorCheck;
  }

  return { collides: false, reason: '', collidingElement: null };
}

module.exports = {
  // Geometry helpers
  getRotatedDimensions,
  getWallAlignedHalfExtents,
  projectOntoWallAxis,
  getOBBCorners,

  // Individual overlap checks
  checkExceedsParentWall,
  checkAdjacentWallOverlap,
  maxWidthClearingAdjacentWalls,
  ADJACENT_WALL_TOLERANCE,
  wallMountedRotationY,
  checkTallObjectOverlap,
  checkOpeningOverlap,
  checkCeilingOverlap,
  checkFloorOverlap,

  // Orchestrator
  checkWallFixtureOverlap,
};
